import { TypedDocumentNode, gql } from '@apollo/client';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Promotion } from '@sorare/core/src/atoms/icons/Promotion';
import { Relegation } from '@sorare/core/src/atoms/icons/Relegation';
import { Reputation } from '@sorare/core/src/atoms/icons/rivals/branded/Reputation';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { rivals } from '@sorare/core/src/lib/glossary';

import { GridDivWithBorder } from '@sorare/gameplay/src/components/rivals/GridBackground/GridDivWithBorder';

import {
  ManagerDivision_footballRivalsDivisionLeaderboardDivisionConfig,
  ManagerDivision_footballRivalsDivisionRanking,
} from './__generated__/index.graphql';

const DivisionLogo = styled.img`
  width: 40px;
  height: 40px;
`;
const Header = styled(Horizontal)`
  width: 100%;
  align-items: flex-end;

  & > :last-child {
    flex: 1;
  }
`;
const TitleWrapper = styled(SBHorizontal)`
  align-items: flex-end;
`;
const StyledGridDivWithBorder = styled(GridDivWithBorder)`
  width: calc(5 * var(--unit));
  height: calc(5 * var(--unit));
  background: var(--c-nd-50);
`;

type Props = {
  ranking?: ManagerDivision_footballRivalsDivisionRanking | null;
  divisionConfig?: ManagerDivision_footballRivalsDivisionLeaderboardDivisionConfig;
  customTitle?: ReactNode;
  withSelectorIcon?: boolean;
};
export const ManagerDivision = ({
  ranking,
  divisionConfig: divisionConfigProp,
  customTitle,
  withSelectorIcon,
}: Props) => {
  const { formatMessage } = useIntl();

  const promotion = ranking?.divisionChange === DivisionChange.PROMOTED;
  const relegation = ranking?.divisionChange === DivisionChange.RELEGATED;

  const divisionConfig = divisionConfigProp || ranking?.divisionConfig;
  const defaultTitle = formatMessage(rivals.divisionNumber, { number: '-' });

  return (
    <Header>
      {divisionConfig?.badgeUrl ? (
        <DivisionLogo src={divisionConfig.badgeUrl} alt="" />
      ) : (
        <StyledGridDivWithBorder borderColor="var(--c-rivals-arena)" />
      )}
      <TitleWrapper>
        <Vertical gap={0.5}>
          <Horizontal gap={1}>
            <LabelM bold>
              {customTitle || divisionConfig?.title || defaultTitle}
            </LabelM>
            {withSelectorIcon && <FontAwesomeIcon icon={faCaretDown} />}
          </Horizontal>
          <LabelM>
            <>
              <span style={{ fontWeight: 'bold' }}>
                {ranking?.ranking || '-'}/
              </span>
              {ranking?.leaderboard?.rankingsCount || '-'}
            </>
          </LabelM>
        </Vertical>
        <Horizontal gap={0.5}>
          {promotion && <Promotion fill="var(--c-rivals-friendlies)" />}
          {relegation && <Relegation />}
          <LabelM bold>
            <Horizontal gap={0}>
              {ranking?.points || '-'}
              <Reputation width={16} hideCount />
            </Horizontal>
          </LabelM>
        </Horizontal>
      </TitleWrapper>
    </Header>
  );
};

const footballRivalsDivisionLeaderboardDivisionConfigFragment = gql`
  fragment ManagerDivision_footballRivalsDivisionLeaderboardDivisionConfig on FootballRivalsDivisionLeaderboardDivisionConfig {
    id
    title
    badgeUrl
  }
` as TypedDocumentNode<ManagerDivision_footballRivalsDivisionLeaderboardDivisionConfig>;

ManagerDivision.fragments = {
  footballRivalsDivisionRanking: gql`
    fragment ManagerDivision_footballRivalsDivisionRanking on FootballRivalsDivisionRanking {
      id
      points
      ranking
      divisionChange
      leaderboard {
        id
        rankingsCount
      }
      divisionConfig {
        id
        ...ManagerDivision_footballRivalsDivisionLeaderboardDivisionConfig
      }
    }
    ${footballRivalsDivisionLeaderboardDivisionConfigFragment}
  ` as TypedDocumentNode<ManagerDivision_footballRivalsDivisionRanking>,
  footballRivalsDivisionLeaderboardDivisionConfig:
    footballRivalsDivisionLeaderboardDivisionConfigFragment,
};
